import { Plus, X } from 'lucide-react'
import { RateconRevisionInvoiceContext } from '@numeo/types-main-web-app'
import { Button, Input, Select } from '@/components/ds2'
import {
    applyBillingType,
    BILLING_TYPE_SECTIONS,
    BillingDraft,
    BillingLineDraft,
    BillingResolution,
    billingLinesTotal,
    billingTypeValue,
    describeBillingOutcome,
    EMPTY_BILLING_LINE,
    formatSignedUsd,
    formatUsd,
    lineAmountText,
    lineKind,
} from '../utils/revisionAdjustments'

interface RateconRevisionBillingPanelProps {
    context: RateconRevisionInvoiceContext
    draft: BillingDraft
    onChange: (draft: BillingDraft) => void
    rateChanged: boolean
    /** The rate accepting would write: the revised one, or the dispatcher's edit-merged one. */
    effectiveRate: number
    resolution: BillingResolution
    disabled?: boolean
}

/** What accepting means once the load is invoiced. The invoice itself is never touched: the load takes the revised terms and the
 *  difference waits in Ready to Invoice, on the load it belongs to, until someone turns it into an invoice. The dispatcher can bill
 *  the whole difference as one charge, or split it into named charges (detention, lumper…) that must still total the difference. */
export const RateconRevisionBillingPanel = ({
    context,
    draft,
    onChange,
    rateChanged,
    effectiveRate,
    resolution,
    disabled,
}: RateconRevisionBillingPanelProps) => {
    const sole = draft.lines.length === 1
    // What is still missing is a prompt until the dispatcher has touched the fields; only then is it an error.
    const touched = draft.lines.some((line) => line.amount !== null || line.kind !== null) || draft.lines.length > 1

    const updateLine = (index: number, patch: Partial<BillingLineDraft>) =>
        onChange({ lines: draft.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) })
    const addLine = () => {
        // The sole line auto-shows the suggested difference; once a second line joins, that figure no longer auto-fills, so it is
        // written into the line first — the dispatcher splits a visible amount rather than watching it vanish.
        const seeded =
            sole && draft.lines[0].amount === null
                ? [{ ...draft.lines[0], amount: lineAmountText(draft.lines[0], context, rateChanged, effectiveRate, true) }]
                : draft.lines
        onChange({ lines: [...seeded, { ...EMPTY_BILLING_LINE, amount: '' }] })
    }
    const removeLine = (index: number) => onChange({ lines: draft.lines.filter((_, i) => i !== index) })

    const linesTotal = billingLinesTotal(draft, context, rateChanged, effectiveRate)
    const difference = Math.round((effectiveRate - context.billedAmount) * 100) / 100

    return (
        <div data-testid="ratecon-revision-billing-panel" className="space-y-2">
            <div className="flex items-center justify-between gap-3 text-xs">
                <span className="min-w-0 truncate text-foreground-muted">
                    <span className="font-semibold text-foreground">{context.originalInvoiceNumber}</span>
                    {context.billToName ? ` · ${context.billToName}` : ''}
                </span>
                <span className="shrink-0 text-foreground-muted">
                    Line haul billed <span className="font-semibold text-foreground">{formatUsd(context.billedAmount)}</span>
                </span>
            </div>

            {draft.lines.map((line, index) => (
                <div key={index} className="flex items-start gap-2" data-testid={`billing-line-${index}`}>
                    <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_8.5rem_minmax(0,1.3fr)]">
                        <Select
                            label={index === 0 ? 'Charge for' : undefined}
                            value={billingTypeValue(lineKind(line, rateChanged, index === 0), line.accessorialType)}
                            placeholder="Choose…"
                            sections={BILLING_TYPE_SECTIONS}
                            onChange={(value) => updateLine(index, applyBillingType(line, value))}
                            disabled={disabled}
                            data-testid={`billing-kind-${index}`}
                        />
                        <Input
                            inputType="number"
                            label={index === 0 ? 'Amount ($)' : undefined}
                            value={lineAmountText(line, context, rateChanged, effectiveRate, sole)}
                            onChange={(e) => updateLine(index, { amount: e.target.value })}
                            disabled={disabled}
                            placeholder="0.00"
                            error={touched && !resolution.ok}
                            data-testid={`billing-amount-${index}`}
                        />
                        <Input
                            label={index === 0 ? 'Description (optional)' : undefined}
                            value={line.note}
                            onChange={(e) => updateLine(index, { note: e.target.value })}
                            disabled={disabled}
                            placeholder="e.g. 2 h at the shipper"
                            data-testid={`billing-note-${index}`}
                        />
                    </div>
                    {!sole && (
                        <button
                            type="button"
                            onClick={() => removeLine(index)}
                            disabled={disabled}
                            aria-label={`Remove charge ${index + 1}`}
                            data-testid={`billing-remove-${index}`}
                            className={`flex size-8 shrink-0 items-center justify-center rounded-xl text-foreground-muted hover:bg-default-hover ${index === 0 ? 'mt-6' : ''}`}
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                    )}
                </div>
            ))}

            <div className="flex items-center justify-between gap-3">
                <Button variant="outline" size="sm" onClick={addLine} disabled={disabled} data-testid="billing-add-line">
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add charge line
                </Button>
                {!sole && (
                    <span className="text-xs text-foreground-muted" data-testid="billing-lines-total">
                        Lines total{' '}
                        <span className={`font-semibold ${linesTotal === difference ? 'text-foreground' : 'text-danger'}`}>{formatSignedUsd(linesTotal)}</span>{' '}
                        · difference <span className="font-semibold text-foreground">{formatSignedUsd(difference)}</span>
                    </span>
                )}
            </div>

            <p
                className={`text-xs leading-4 ${resolution.ok || !touched ? 'text-foreground-muted' : 'text-danger'}`}
                data-testid="billing-outcome"
                role={resolution.ok || !touched ? undefined : 'alert'}
            >
                {describeBillingOutcome(context, resolution, rateChanged, effectiveRate)}
                {context.openAdjustment && resolution.ok && (
                    <span data-testid="billing-open-adjustment"> It replaces the {formatUsd(context.openAdjustment.amount)} already waiting on this load.</span>
                )}
            </p>
        </div>
    )
}
